import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown, Search } from 'lucide-react';
import { useI18n, LanguageCode, LanguageOption } from '../utils/i18n';

interface LanguageSelectorProps {
  compact?: boolean;
  dropUp?: boolean;
  fullWidth?: boolean;
  align?: 'left' | 'right' | 'full';
}

export function LanguageSelector({ 
  compact = false, 
  dropUp = false, 
  fullWidth = false,
  align = 'full'
}: LanguageSelectorProps) {
  const { lang, changeLanguage, supportedLanguages, currentLanguageOption } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const filteredLanguages = supportedLanguages.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    return (
      l.label.toLowerCase().includes(q) ||
      l.nativeLabel.toLowerCase().includes(q) ||
      l.region.toLowerCase().includes(q) ||
      l.code.toLowerCase().includes(q)
    );
  });

  if (compact) {
    const popoverPositionClass = align === 'full'
      ? 'left-0 right-0 w-full'
      : align === 'left'
      ? 'left-0 w-64 max-w-[calc(100vw-2rem)]'
      : 'right-0 w-64 max-w-[calc(100vw-2rem)]';

    return (
      <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`px-3 py-2 rounded-xl bg-[#151819] hover:bg-[#1A1D1F] border border-[#2A2F31] hover:border-[#14B8A6]/50 text-gray-200 hover:text-white transition-all text-xs flex items-center justify-between gap-2.5 cursor-pointer shadow-xs group ${fullWidth ? 'w-full' : ''}`}
          title="Change interface language"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-base shrink-0 leading-none">{currentLanguageOption.flag}</span>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-bold text-xs text-white truncate">{currentLanguageOption.nativeLabel}</span>
              <span className="text-[11px] text-gray-400 truncate hidden xs:inline">({currentLanguageOption.region})</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 text-gray-400 group-hover:text-white">
            <span className="text-[10px] font-mono font-bold uppercase text-gray-500">{currentLanguageOption.code}</span>
            <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? (dropUp ? '-rotate-180 text-[#14B8A6]' : 'rotate-180 text-[#14B8A6]') : ''}`} />
          </div>
        </button>

        {isOpen && (
          <div className={`absolute ${popoverPositionClass} ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'} bg-[#121516] border border-[#2A2F31] rounded-xl shadow-2xl z-[120] overflow-hidden animate-in fade-in zoom-in-95 duration-100 p-2.5`}>
            <div className="p-2 border border-[#2A2F31] bg-[#0C0E0F] rounded-lg flex items-center gap-2 mb-2">
              <Search size={13} className="text-gray-400 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search languages..."
                className="w-full bg-transparent text-xs text-white placeholder-gray-500 focus:outline-none"
                autoFocus
              />
            </div>

            <div className="max-h-56 overflow-y-auto space-y-1 custom-scrollbar pr-0.5">
              {filteredLanguages.map((l: LanguageOption) => {
                const isActive = lang === l.code;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => {
                      changeLanguage(l.code);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-between gap-3 cursor-pointer ${
                      isActive
                        ? 'bg-[#14B8A6]/20 text-[#14B8A6] font-bold border border-[#14B8A6]/40'
                        : 'text-gray-300 hover:bg-[#1C2022] hover:text-white border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-lg shrink-0 leading-none">{l.flag}</span>
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold text-xs leading-tight truncate">{l.nativeLabel}</span>
                        <span className="text-[10px] text-gray-500 leading-tight truncate">{l.label} • {l.region}</span>
                      </div>
                    </div>
                    {isActive && <Check size={14} className="text-[#14B8A6] shrink-0" />}
                  </button>
                );
              })}
              {filteredLanguages.length === 0 && (
                <div className="p-4 text-center text-xs text-gray-500">
                  No language matches "{search}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
        {supportedLanguages.map((l) => {
          const isActive = lang === l.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => changeLanguage(l.code)}
              className={`p-3 rounded-xl text-left transition-all flex items-center justify-between gap-3 border cursor-pointer ${
                isActive
                  ? 'bg-[#14B8A6]/15 border-[#14B8A6] text-white shadow-xs ring-1 ring-[#14B8A6]/30'
                  : 'bg-[#0C0E0F] border-[#2A2F31] text-gray-300 hover:text-white hover:border-[#14B8A6]/40 hover:bg-[#151819]'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-2xl shrink-0 p-1 bg-[#151819] rounded-lg border border-[#2A2F31]">{l.flag}</span>
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-xs text-white">{l.nativeLabel}</span>
                    {l.code !== 'en' && <span className="text-[10px] text-gray-500 font-mono uppercase">({l.code})</span>}
                  </div>
                  <span className="text-[11px] text-gray-400 truncate">{l.label}</span>
                  <span className="text-[9px] text-[#14B8A6] font-mono mt-0.5">{l.region}</span>
                </div>
              </div>
              {isActive && (
                <div className="w-5 h-5 rounded-full bg-[#14B8A6] text-black flex items-center justify-center shrink-0">
                  <Check size={12} className="stroke-[3]" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
